import { z, uuidSchema } from '@kc3/validation';
import { baseEventFields } from './_base.js';
import { CallOutcomeSchema } from '../enums/index.js';
const callerIdentifierSchema = z.string().regex(/^\+[1-9]\d{6,14}$|^(?:visitor|visitor-call)[-_a-zA-Z0-9]+$/);
export const CallIncomingEventSchema = z.object({
    type: z.literal('call.incoming'),
    ...baseEventFields,
    callId: uuidSchema,
    from: callerIdentifierSchema,
    to: callerIdentifierSchema,
    channelId: z.string().min(1),
    /**
     * Resolved `clients.full_name` for a client-portal Call Support visitor
     * (`from` matching the `visitor`/`visitor-call` pattern above) — `from`
     * itself must stay a stable identifier the rest of the call pipeline can
     * parse, so the human-readable name travels separately. Undefined for
     * ordinary PSTN calls and for a visitor with no name on file.
     */
    callerDisplayName: z.string().min(1).optional(),
    /** Resolved `clients.email` for the same visitor. Undefined for the same reasons as callerDisplayName. */
    callerEmail: z.string().min(1).optional(),
    /** Resolved `clients.id`, so the agent UI can fetch this client's call history. Undefined for the same reasons as callerDisplayName. */
    clientId: uuidSchema.optional(),
    /**
     * Set when an AI voice agent hands the caller to a queue: the
     * voice_agent_sessions id (full transcript), the AI's summary for the agent
     * and why it transferred. Undefined for every other call.
     */
    aiSessionId: uuidSchema.optional(),
    aiSummary: z.string().max(2000).optional(),
    aiHandoffReason: z.string().max(500).optional(),
});
export const CallAnsweredEventSchema = z.object({
    type: z.literal('call.answered'),
    ...baseEventFields,
    callId: uuidSchema,
    agentId: uuidSchema,
    answeredAt: z.string().datetime(),
});
export const CallEndedEventSchema = z.object({
    type: z.literal('call.ended'),
    ...baseEventFields,
    callId: uuidSchema,
    endedAt: z.string().datetime(),
    durationSeconds: z.number().int().nonnegative(),
    outcome: CallOutcomeSchema,
    /** Visitor identity for client-portal calls, allowing direct widget relay without cache lookup. */
    visitorId: uuidSchema.optional(),
});
export const CallIvrStartEventSchema = z.object({
    type: z.literal('call.ivr.start'),
    ...baseEventFields,
    callId: uuidSchema,
    channelId: z.string().min(1),
    ivrMenuId: uuidSchema,
});
export const CallStateTransitionEventSchema = z.object({
    type: z.literal('call.state_transition'),
    ...baseEventFields,
    callId: uuidSchema,
    from: z.string().min(1),
    to: z.string().min(1),
    at: z.string().datetime(),
});
/**
 * `call.pci.pause` — supervisor/agent paused recording for PCI capture
 * (Phase 4 Task 6). The worker handler (a) calls `AriPool.recordPause`,
 * (b) marks a `pciPauseStart` ms-since-recording-start offset on call
 * state. The matching `call.pci.resume` finalises the segment.
 */
export const CallPciPauseEventSchema = z.object({
    type: z.literal('call.pci.pause'),
    ...baseEventFields,
    callId: uuidSchema,
    channelId: z.string().min(1),
    agentId: uuidSchema,
    startMs: z.number().int().nonnegative(),
});
/**
 * `call.pci.resume` — supervisor/agent resumed recording after a PCI
 * pause (Phase 4 Task 6). The worker handler appends a
 * `{startOffsetMs, endOffsetMs}` segment to `recordings.pci_pause_segments`.
 */
export const CallPciResumeEventSchema = z.object({
    type: z.literal('call.pci.resume'),
    ...baseEventFields,
    callId: uuidSchema,
    channelId: z.string().min(1),
    agentId: uuidSchema,
    startMs: z.number().int().nonnegative(),
    endMs: z.number().int().nonnegative(),
});
//# sourceMappingURL=call.js.map